import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Briefcase, MapPin, Mail, Shield } from 'lucide-react';

const DISTRITOS = [
  { id: '13', label: 'Distrito 13' },
  { id: '17', label: 'Distrito 17' },
  { id: '18', label: 'Distrito 18' },
  { id: '19', label: 'Distrito 19' },
  { id: '23', label: 'Distrito 23' },
  { id: '30', label: 'Distrito 30' },
];

const emptyForm = { name: '', email: '', phone: '', password: '', distrito: '', secciones: '' };

export default function FuncionariosPage() {
  const { api } = useAuth();
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/funcionarios');
      setFuncionarios(res.data);
    } catch (e) {
      toast.error('No se pudieron cargar los funcionarios');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (f) => {
    setEditing(f);
    setForm({
      name: f.name, email: f.email, phone: f.phone || '', password: '',
      distrito: f.distrito || '',
      secciones: (f.secciones || []).join(', ')
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.distrito) {
      toast.error('Completa nombre, email y distrito');
      return;
    }
    if (!editing && !form.phone && !form.password) {
      toast.error('Agrega al menos un método de acceso: teléfono WhatsApp o contraseña');
      return;
    }
    const payload = {
      name: form.name,
      email: form.email,
      password: form.password,
      phone: form.phone,
      distrito: form.distrito,
      secciones: form.secciones.split(',').map(s => s.trim()).filter(Boolean),
    };
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/funcionarios/${editing.id}`, payload);
        toast.success('Funcionario actualizado');
      } else {
        await api.post('/funcionarios', payload);
        toast.success('Funcionario creado');
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/funcionarios/${confirmDelete.id}`);
      toast.success('Funcionario eliminado');
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-6" data-testid="funcionarios-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Funcionarios</h1>
          <p className="text-base text-[#52525B] mt-1">Crea y gestiona funcionarios y sus distritos asignados</p>
        </div>
        <Button data-testid="new-funcionario-btn" onClick={openNew} className="bg-[#0052FF] hover:bg-[#0048E5] text-white rounded-md h-11 px-5">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Funcionario
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-8 h-8 border-2 border-[#0052FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : funcionarios.length === 0 ? (
        <Card className="border-dashed border-[#E4E4E7]">
          <CardContent className="py-16 text-center">
            <Shield className="w-12 h-12 text-[#E4E4E7] mx-auto mb-4" />
            <p className="text-lg font-semibold text-[#0A0A0A]">Sin funcionarios</p>
            <p className="text-sm text-[#52525B]">Crea el primero para comenzar a estructurar la jerarquía</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {funcionarios.map(f => (
            <Card data-testid={`funcionario-card-${f.id}`} key={f.id} className="bg-white border border-[#E4E4E7] rounded-md hover:-translate-y-0.5 hover:shadow-md hover:border-[#10B981] transition-all duration-200">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-md bg-[#0052FF]/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-[#0052FF]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-base font-bold text-[#0A0A0A] truncate">{f.name}</p>
                      <p className="text-xs text-[#52525B] flex items-center gap-1 truncate">
                        <Mail className="w-3 h-3" /> {f.email}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs border-[#0052FF] text-[#0052FF] flex-shrink-0">
                    <MapPin className="w-3 h-3 mr-1" /> {f.distrito ? `D${f.distrito}` : 'S/D'}
                  </Badge>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="p-2 rounded-md bg-[#FAFAFA] text-center">
                    <p className="text-lg font-black text-[#0A0A0A]">{f.brigadistas_count || 0}/10</p>
                    <p className="text-[10px] uppercase tracking-wider text-[#52525B] font-bold">Brigadistas</p>
                  </div>
                  <div className="p-2 rounded-md bg-[#FAFAFA] text-center">
                    <p className="text-lg font-black text-[#0A0A0A]">{f.gestores_count || 0}/2</p>
                    <p className="text-[10px] uppercase tracking-wider text-[#52525B] font-bold">Gestores</p>
                  </div>
                  <div className="p-2 rounded-md bg-[#10B981]/10 text-center">
                    <p className="text-lg font-black text-[#10B981]">{f.gestiones_count || 0}</p>
                    <p className="text-[10px] uppercase tracking-wider text-[#52525B] font-bold">Gestiones</p>
                  </div>
                </div>

                {f.secciones?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {f.secciones.slice(0, 6).map(s => (
                      <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-[#FAFAFA] border border-[#E4E4E7] text-[#52525B] font-mono">{s}</span>
                    ))}
                    {f.secciones.length > 6 && <span className="text-[10px] text-[#52525B]">+{f.secciones.length - 6}</span>}
                  </div>
                )}

                <div className="flex gap-2 pt-2 border-t border-[#E4E4E7]">
                  <Button data-testid={`edit-funcionario-${f.id}`} variant="outline" size="sm" onClick={() => openEdit(f)} className="flex-1 rounded-md">
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Editar
                  </Button>
                  <Button data-testid={`delete-funcionario-${f.id}`} variant="outline" size="sm" onClick={() => setConfirmDelete(f)} className="flex-1 rounded-md text-[#EF4444] hover:bg-[#EF4444]/10 hover:text-[#EF4444] hover:border-[#EF4444]">
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Eliminar
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="funcionario-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Funcionario' : 'Nuevo Funcionario'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Actualiza los datos. Deja vacía la contraseña para conservarla.' : 'Crea un funcionario asignándole un distrito CDMX.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Nombre completo</Label>
              <Input data-testid="form-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Maria López" />
            </div>
            <div>
              <Label>Email (para Google Login)</Label>
              <Input data-testid="form-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="funcionario@ejemplo.com" />
            </div>
            <div>
              <Label>Teléfono WhatsApp (para OTP)</Label>
              <Input data-testid="form-phone" type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+5215512345678" />
              <p className="text-[10px] text-[#52525B] mt-1">Uno de los dos: Email + Google o Teléfono + WhatsApp OTP</p>
            </div>
            <div>
              <Label>{editing ? 'Nueva contraseña (opcional)' : 'Contraseña (opcional)'}</Label>
              <Input data-testid="form-password" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="••••••••" />
            </div>
            <div>
              <Label>Distrito CDMX</Label>
              <Select value={form.distrito} onValueChange={v => setForm({...form, distrito: v})}>
                <SelectTrigger data-testid="form-distrito"><SelectValue placeholder="Selecciona un distrito" /></SelectTrigger>
                <SelectContent>
                  {DISTRITOS.map(d => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Secciones electorales (separadas por coma)</Label>
              <Input data-testid="form-secciones" value={form.secciones} onChange={e => setForm({...form, secciones: e.target.value})} placeholder="0101, 0205, 0310" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="form-submit" type="submit" disabled={saving} className="bg-[#0052FF] hover:bg-[#0048E5] text-white">
                {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent data-testid="confirm-delete-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¿Eliminar funcionario?</DialogTitle>
            <DialogDescription>
              Esta acción eliminará a <strong>{confirmDelete?.name}</strong> y a todos sus brigadistas y gestores asociados. No se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancelar</Button>
            <Button data-testid="confirm-delete-btn" onClick={handleDelete} className="bg-[#EF4444] hover:bg-[#DC2626] text-white">Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
